import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JwtHelperService, JWT_OPTIONS  } from '@auth0/angular-jwt';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorStateMatcher, MatNativeDateModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import * as _ from 'lodash';
import { LayoutModule } from '@angular/cdk/layout';

// MATERIAL
import {MatIconModule} from '@angular/material/icon';
import {MAT_FORM_FIELD_DEFAULT_OPTIONS} from '@angular/material/form-field';
import {MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig} from '@angular/material/dialog';
import {MatSnackBarModule, MAT_SNACK_BAR_DEFAULT_OPTIONS} from '@angular/material/snack-bar';

//SERVICES
import { LocaleService } from './services/locale.service';
import { ToastService } from './services/toast.service';
import { RollbarService } from './services/rollbar.service';
import { AuthService } from './services/auth.service';

//DIRECTIVES
import { NumbersOnlyDirective } from './directives/numbers-only/numbers-only.directive';

//INTERCEPTORS
import { SpinnerInterceptor } from './interceptors/spinner.interceptor';
import { AuthInterceptor } from './interceptors/auth.interceptor';

import { customErrorStateMatcher } from './util/config/error-state-matcher.config';
import { SNACK_BAR_DEFAULT_OPTIONS } from './consts/snack-bar-default-options.const';
import { DISABLED_SPINNER_PATHS } from './tokens/disabled-spinner-paths.token';
import { DISABLED_SPINNER_ENDPOINTS } from './consts/disabled-spinner-endpoints.const';
import { RollbarInterceptor } from './interceptors/rollbar.interceptor';

//COMPONENTS
import { FooterComponent } from './components/footer/footer.component';

//MODULES
import { SharedModule } from './modules/shared/shared.module';

export function setupTranslateFactory(service: LocaleService): () => any {
  return () => service.setupTranslate();
}

export function initializeAuthenticationFactory(service: AuthService): () => Promise<void> {
  return () => service.initializeSession();
}

@NgModule({
  declarations: [
    AppComponent,
    FooterComponent,
    NumbersOnlyDirective,
  ],
  imports: [
    AppRoutingModule,
    MatIconModule,
    CommonModule,
    LayoutModule,
    BrowserModule,
    SharedModule,
    BrowserAnimationsModule,
    MatSnackBarModule,
    MatNativeDateModule,
  ],
  providers: [
    { provide: JWT_OPTIONS, useValue: JWT_OPTIONS },
    JwtHelperService,
    ToastService,
    RollbarService,
    {
      provide: APP_INITIALIZER,
      useFactory: setupTranslateFactory,
      deps: [ LocaleService ],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuthenticationFactory,
      deps: [ AuthService ],
      multi: true
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        ...new MatDialogConfig(),
        width: '80%',
        maxWidth: '460px',
        autoFocus: false,
        restoreFocus: false,
      } as MatDialogConfig,
    },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: SNACK_BAR_DEFAULT_OPTIONS },
    { provide: DISABLED_SPINNER_PATHS, useValue: DISABLED_SPINNER_ENDPOINTS },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SpinnerInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RollbarInterceptor, multi: true },
    { provide: ErrorStateMatcher, useClass: customErrorStateMatcher },
    { provide: ErrorHandler, useClass: RollbarService },
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
